import { useState } from 'react'
import { ShieldQuestion } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'
import type { StorePendingDevice } from '../hooks/useStoreDevicePendingApprovals'

interface Props {
  open: boolean
  pending: StorePendingDevice[]
  /** approve/deny 後に呼ぶ (pending を再取得 → 空で gate 解除)。 */
  onChanged: () => void
}

/**
 * 店舗オーナー向け初見デバイス承認 (triage) モーダル (#867 v3+).
 *
 * 顧客側 `DeviceApprovalPromptModal` (#819) の店舗版。trusted device で
 * pending 端末を approve / deny する。i18n keys は顧客側と共有。
 *
 * deny は破壊操作 (全 credential 失効) なので window.confirm で確認を挟む
 * (memory `feedback_ato_detection_out_of_band`)。
 */
export default function StoreDeviceApprovalPromptModal({ open, pending, onChanged }: Props) {
  const { t, locale } = useI18n()
  const [busyId, setBusyId] = useState<string | null>(null)

  if (!open) return null

  const fmt = (v: unknown): string => {
    if (!v) return '-'
    try {
      if (typeof v === 'string') {
        const d = new Date(v)
        return isNaN(d.getTime()) ? v : d.toLocaleString(locale)
      }
      const s = (v as { _seconds?: number })._seconds
      if (typeof s === 'number') return new Date(s * 1000).toLocaleString(locale)
    } catch {
      /* noop */
    }
    return '-'
  }

  const caller = (): string => {
    try {
      return getDeviceId()
    } catch {
      return ''
    }
  }

  const handleApprove = async (deviceId: string) => {
    if (busyId) return
    setBusyId(deviceId)
    try {
      await api.approveStoreDevice(deviceId, caller())
      showToast({ type: 'success', message: t('deviceApproval.approvedToast') })
      onChanged()
    } catch (e) {
      console.error('[StoreDeviceApproval] approve failed:', e)
      showToast({ type: 'error', message: t('deviceApproval.actionError') })
    } finally {
      setBusyId(null)
    }
  }

  const handleDeny = async (deviceId: string) => {
    if (busyId) return
    if (!window.confirm(t('deviceApproval.denyConfirm'))) return
    setBusyId(deviceId)
    try {
      await api.denyStoreDevice(deviceId, caller())
      showToast({ type: 'success', message: t('deviceApproval.deniedToast') })
      onChanged()
    } catch (e) {
      console.error('[StoreDeviceApproval] deny failed:', e)
      showToast({ type: 'error', message: t('deviceApproval.actionError') })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100">
          <ShieldQuestion size={32} className="text-amber-600" />
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t('deviceApproval.promptTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-5 leading-relaxed">
          {t('deviceApproval.promptDescription')}
        </p>

        <div className="space-y-3">
          {pending.map((p) => (
            <div key={p.deviceId} className="rounded-lg bg-gray-50 border border-gray-200 p-3">
              <div className="text-xs font-body text-text-primary space-y-1 mb-3">
                <div>
                  <span className="text-text-secondary">{t('deviceApproval.deviceLabel')}: </span>
                  {p.deviceLabel || '-'}
                </div>
                <div>
                  <span className="text-text-secondary">{t('deviceApproval.ipLabel')}: </span>
                  {p.lastIpAddress || '-'}
                </div>
                <div>
                  <span className="text-text-secondary">{t('deviceApproval.seenLabel')}: </span>
                  {fmt(p.firstSeen)}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprove(p.deviceId)}
                  disabled={busyId !== null}
                  className={`flex-1 h-11 rounded-xl font-display font-bold text-sm transition-colors ${
                    busyId !== null
                      ? 'bg-gray-300 text-white cursor-not-allowed'
                      : 'bg-yokoso-red text-white tap-effect'
                  }`}
                >
                  {t('deviceApproval.approveAction')}
                </button>
                <button
                  onClick={() => handleDeny(p.deviceId)}
                  disabled={busyId !== null}
                  className="flex-1 h-11 rounded-xl border border-amber-300 font-body text-sm font-semibold text-amber-700 tap-effect disabled:opacity-50"
                >
                  {t('deviceApproval.denyAction')}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="font-body text-[11px] text-text-secondary text-center mt-4 leading-relaxed">
          {t('deviceApproval.promptHint')}
        </p>
      </div>
    </div>
  )
}
